import { action, makeObservable, observable } from 'mobx';
import { CachedItem } from '@DStvMonoRepo/self-service-core';
import { DefaultFeatureFlags, DevFeatureFlags } from 'core/constants/default-feature-flags';
import { FeaturePermission } from 'core/constants/feature-permissions';
import { FeatureFlag } from 'core/enums/feature-flag';
import { Feature } from 'core/enums/features';
import { GenexSurveyTypes } from 'core/enums/genex';
import { PermissionType } from 'core/enums/permission-type';
import { FeatureService } from 'core/services/api/feature';
import { Util } from 'utils/common.utils';
import { PaymentPlatform } from './payment/payment.store';

export class FeatureStore {
  isLoading = true;
  region!: string;
  permissionMap: Map<string, PermissionType> = new Map();

  get inMaintenanceMode() {
    return this.hasViewPermission(FeatureFlag.ScheduledMaintenancePanel)
      ? FeatureFlag.ScheduledMaintenancePanel
      : this.hasViewPermission(FeatureFlag.UnexpectedMaintenancePanel)
      ? FeatureFlag.UnexpectedMaintenancePanel
      : false;
  }

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      permissionMap: observable,
      changeLoadingState: action,
      changePermissionMap: action,
    });
  }

  initialize(countryCode: string, platform: string) {
    return this.fetchFeatureMaps(countryCode, platform);
  }

  changeLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  changePermissionMap(permissionMap: Map<string, PermissionType>) {
    this.permissionMap = permissionMap;
  }

  fetchFeatureMaps(countryCode: string, platform: string) {
    if (this.region === countryCode) {
      return;
    }

    this.region = countryCode;
    this.changeLoadingState(true);

    const featureFlagOverride = new CachedItem('features', true);
    if (featureFlagOverride.get()) {
      const features = Util.ObjectToMap<string, PermissionType>({
        ...FeatureService.mapFeaturesToObject(featureFlagOverride.get()),
        ...DefaultFeatureFlags,
      });
      this.changePermissionMap(features);
      this.changeLoadingState(false);
      return Promise.resolve();
    }

    return FeatureService.getFeatures(`en-${countryCode}`, platform)
      .then((flags) => {
        const features = !flags
          ? Util.ObjectToMap<string, PermissionType>({ ...DevFeatureFlags })
          : Util.ObjectToMap<string, PermissionType>({ ...flags, ...DefaultFeatureFlags });
        this.changePermissionMap(features);
        this.changeLoadingState(false);
      })
      .catch((err) => {
        console.error('Failed to set feature flags.');
        this.changePermissionMap(
          Util.ObjectToMap<string, PermissionType>({ [FeatureFlag.UnexpectedMaintenancePanel]: PermissionType.View })
        ); // FIXME: Remove this once feature flags are stable
        this.changeLoadingState(false);
      });
  }

  static hasRoutePermission(permissionMap: Map<string, PermissionType>, feature: Feature): boolean {
    return (
      !FeaturePermission[feature] ||
      (FeaturePermission[feature] as FeatureFlag[]).some(
        (permission) => permissionMap?.get(`${permission}`) === PermissionType.Route
      )
    );
  }

  hasRoutePermission(feature: Feature): boolean {
    return (
      !FeaturePermission[feature] ||
      (FeaturePermission[feature] as FeatureFlag[]).some(
        (permission) => this.permissionMap?.get(`${permission}`) === PermissionType.Route
      )
    );
  }

  static hasEditPermission(permissionMap: Map<string, PermissionType>, permission: FeatureFlag): boolean {
    const value = permissionMap?.get(`${permission}`);
    return !!value && (value as PermissionType) >= PermissionType.Edit;
  }

  hasEditPermission(permission: FeatureFlag): boolean {
    const value = this.permissionMap?.get(`${permission}`);
    return !!value && (value as PermissionType) >= PermissionType.Edit;
  }

  static hasViewPermission(permissionMap: Map<string, PermissionType>, permission: FeatureFlag): boolean {
    const value = permissionMap?.get(`${permission}`);
    return !!value && (value as PermissionType) >= PermissionType.View;
  }

  hasViewPermission(permission: FeatureFlag): boolean {
    const value = this.permissionMap?.get(`${permission}`);
    return !!value && (value as PermissionType) >= PermissionType.View;
  }

  getPaymentPlatform() {
    if (this.hasViewPermission(FeatureFlag.UseDPPPayment)) {
      return PaymentPlatform.DPP;
    }
    if (this.hasViewPermission(FeatureFlag.UsePayAtExternalPayment)) {
      return PaymentPlatform.PayAtExternal;
    }
    if (this.hasViewPermission(FeatureFlag.UsePayAtRedirectPayment)) {
      return PaymentPlatform.PayAtRedirect;
    }
    if (this.hasViewPermission(FeatureFlag.UseRapydRedirectPayment)) {
      return PaymentPlatform.RapydRedirect;
    }
    if (this.hasViewPermission(FeatureFlag.UseRapydEmbeddedPayment)) {
      return PaymentPlatform.RapydEmbedded;
    }
    if (this.hasViewPermission(FeatureFlag.UseDirectLinkPayment)) {
      return PaymentPlatform.PaymentRequestDirectLink;
    }
    if (this.hasViewPermission(FeatureFlag.UseMomentPayment)) {
      return PaymentPlatform.PaymentRequestMoment;
    }
    if (this.hasViewPermission(FeatureFlag.UseMomentCheckoutPayment)) {
      return PaymentPlatform.PaymentRequestMomentCheckout;
    }
    // Default
    return PaymentPlatform.DPP;
  }

  getGenexPlatform() {
    if (this.hasViewPermission(FeatureFlag.UseGenexSurveys)) {
      if (this.hasViewPermission(FeatureFlag.UseMAHGenexDemographics)) {
        return GenexSurveyTypes.GenexMAH;
      }

      return GenexSurveyTypes.Genex;
    }
    return null;
  }

  getHaptikFlag() {
    // return true;
    return this.hasViewPermission(FeatureFlag.UseHaptikBot);
  }

  static getFeaturePermissions(feature: Feature, map: Map<string, PermissionType>): Map<string, PermissionType> {
    return (FeaturePermission[feature] || ([] as FeatureFlag[])).reduce((prev, flag: FeatureFlag) => {
      const type = this.getCurrentPermissionType(flag, map);
      if (type !== undefined) {
        prev.set(flag, type);
      }
      return prev;
    }, new Map<string, PermissionType>());
  }

  static getCurrentPermissionType(flag: string, map: Map<string, PermissionType>): PermissionType | undefined {
    return map?.get(`${flag}`);
  }
}